import {
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "react-hook-form";
import { MultiSelect } from "react-hook-form-mantine";
import {
  useAddSponsorsToGiveaway,
  useGetGiveawayStats,
  useGetSponsorCodes,
  useRemoveSponsorFromGiveaway,
} from "../../api/giveaways";
import { web_addSponsorsToGiveawaySchema } from "@congrs/schemas";
import { IconPrinter } from "@tabler/icons-react";
import { useState } from "react";
import resolver from "@congrs/resolver";
import { useDisclosure } from "@mantine/hooks";
import { createQrCode, fetchImageAsBlobUrl } from "../../utils/createQRcode";
import { printMultipleQRCodes } from "../../utils/printMultipleQRCodes";
import QRCodeModal from "../QRCodeModal";
import GiveawaySponsorCard from "./GiveawaySponsorCard";
import { webConfig } from "../../../config";

const handlePrintMultipleSponsors = async (sponsors, codeMap) => {
  // Create QR codes for all assigned sponsors
  const qrCodesData = await Promise.all(
    sponsors.map(async (sponsor) => {
      const url = `${webConfig.mobileScheme}://giveaways/${codeMap[sponsor.id]}`;
      const blobUrl = sponsor.logo
        ? await fetchImageAsBlobUrl(sponsor.logo)
        : null;
      const qrCode = createQrCode(2000, url, blobUrl);
      return {
        qrCode,
        blobUrl,
        sponsor: {
          name: sponsor.name,
          id: sponsor.id,
          logo: sponsor.logo,
        },
      };
    })
  );

  // Extract just the QR codes and sponsor info for the print function
  const qrCodes = qrCodesData.map((item) => item.qrCode);
  const sponsorInfo = qrCodesData.map((item) => item.sponsor);

  await printMultipleQRCodes(qrCodes, sponsorInfo, "Giveaway QR Codes");

  // Revoke blob URLs after print task completes
  qrCodesData.forEach(({ blobUrl }) => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  });
};

const GiveawaySponsorsForm = ({ sponsors, giveaway }) => {
  const [sponsorData, setSponsorData] = useState({
    id: null,
    url: "",
    logo: "",
  });

  const [opened, { open, close }] = useDisclosure(false);

  const { data: sponsorCodes, isPending: codesPending } = useGetSponsorCodes(
    giveaway?.id
  );

  const codeMap = (sponsorCodes || []).reduce(
    (acc, { sponsorId, uniqueCode }) => {
      acc[sponsorId] = uniqueCode;
      return acc;
    },
    {}
  );

  const handleOpenSponsorQRCodeModal = (sponsor) => {
    setSponsorData({
      id: sponsor.id,
      name: sponsor.name,
      logo: sponsor.logo,
      url: `${webConfig.mobileScheme}://giveaways/${codeMap[sponsor.id]}`,
    });
    open();
  };

  const isDisabled = giveaway?.status === "live";
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      sponsorIds: [],
    },
    resolver: resolver(web_addSponsorsToGiveawaySchema),
  });

  const availableSponsors =
    sponsors?.data?.filter(
      (sponsor) => !giveaway?.sponsorIds?.includes(sponsor.id)
    ) || [];

  const assignedSponsors =
    sponsors?.data?.filter((sponsor) =>
      giveaway?.sponsorIds?.includes(sponsor.id)
    ) || [];

  const { mutate: addSponsors } = useAddSponsorsToGiveaway({
    id: giveaway?.id,
    eventId: giveaway?.eventId,
    sponsorId: sponsorData.id,
  });

  const { mutate: removeSponsor } = useRemoveSponsorFromGiveaway({
    id: giveaway?.id,
    eventId: giveaway?.eventId,
    sponsorId: sponsorData.id,
  });

  const { data: stats } = useGetGiveawayStats(giveaway?.id);
  const totalParticipants = stats?.totalParticipants || 0;

  const handleRemoveSponsor = async (sponsorId) => {
    setSponsorData((prev) => ({
      ...prev,
      id: sponsorId,
    }));

    removeSponsor(sponsorId);
  };

  const handleFormSubmit = async (data) => {
    const payload = {
      ...data,
      sponsorIds: data.sponsorIds.map(Number),
    };

    addSponsors(payload, {
      onSuccess: () => {
        reset();
      },
    });
  };

  return (
    <Container py="md" w={600}>
      <Paper withBorder shadow="md" p="md" px="lg" radius="md">
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <Stack gap="md">
            <Group justify="space-between">
              <Title order={3}>Giveaway sponsors</Title>
              <Tooltip label="Print all sponsors QR codes">
                <Button
                  variant="light"
                  color="green"
                  leftSection={<IconPrinter size={18} />}
                  onClick={() =>
                    handlePrintMultipleSponsors(giveaway?.sponsors, codeMap)
                  }
                  disabled={assignedSponsors.length === 0 || codesPending}
                  radius="md"
                >
                  Print QR codes
                </Button>
              </Tooltip>
            </Group>
            {isDisabled && (
              <Text c="dimmed" size="sm" fs="italic">
                Giveaway is {giveaway?.status}. Adding or removing sponsors is
                not possible.
              </Text>
            )}

            {!isDisabled && (
              <>
                <MultiSelect
                  name="sponsorIds"
                  control={control}
                  placeholder="Pick value"
                  data={availableSponsors.map((sponsor) => {
                    return { value: String(sponsor.id), label: sponsor.name };
                  })}
                  disabled={isDisabled}
                />
                <Button disabled={!isDirty} type="submit">
                  Submit
                </Button>
              </>
            )}
          </Stack>
        </form>
        {assignedSponsors.length > 0 && (
          <>
            <Divider
              label="Assigned Sponsors"
              labelPosition="center"
              mt={20}
              mb={20}
            />
            <Stack gap="md">
              <SimpleGrid cols={1} spacing="sm">
                {giveaway?.sponsors.map((sponsor) => (
                  <GiveawaySponsorCard
                    key={sponsor.id}
                    sponsor={sponsor}
                    totalParticipants={totalParticipants}
                    handleOpenSponsorQRCodeModal={handleOpenSponsorQRCodeModal}
                    isDisabled={isDisabled || sponsor.scanCount > 0}
                    qrDisabled={codesPending}
                    handleRemoveSponsor={handleRemoveSponsor}
                  />
                ))}
              </SimpleGrid>
            </Stack>
          </>
        )}
      </Paper>
      {/* Modal */}
      <QRCodeModal
        opened={opened}
        close={close}
        title={sponsorData.name}
        description={"Scan this code to participate in giveaway"}
        url={sponsorData.url}
        logo={sponsorData.logo}
      />
    </Container>
  );
};

export default GiveawaySponsorsForm;
